import { Vector3 } from 'three';

import { Skeleton } from '../skeleton/core';
import {
  CHAIN_DEFINITIONS,
  ROLE_TERMS,
  detectSideConvention,
  sideConventionIsValid,
  sideConventionDescribe,
  sideConventionUnknown,
  detectSideFromNameWithConvention,
  isClavicleLikeName,
  isHeadDecorativeName,
  isHandLikeName,
  isPelvisLikeName,
  isSimpleHeadSegmentName,
  isStructuralArmAuxiliaryName,
  isStructuralArmChainName,
  isStructuralLegAuxiliaryName,
  isTerminalHelperLikeName,
  isToeRoleCompatibleName,
  isTwistLikeName,
  type SideConvention,
} from '../schema/mapping';
import { SolverType } from '../schema/solver';
import { getChainRoleAssignments } from '../mapping/chainRoleAssignments';
import {
  confidenceTier,
  lateralSpreadScore,
  centerAlignmentScore,
  jointPrebendRatio,
  NamingMode,
  getProfileForMode,
  detectNamingMode,
  type ScoringProfile,
} from './scoring';
import {
  computeSpatialData,
  computeSpatialLandmarks,
  SkeletonSpatialData,
  type SpatialLandmarks,
} from './spatial';
import { detectRigPreset } from './rigPresets';
import { buildResultFromRigPreset } from './rigPresetBuilder';
import {
  MappingResult,
  RecognizerConfig,
  CandidateInfo,
  type ChainResult,
} from './config';
import { ChainMapping } from '../schema/mapping';
import {
  markAuxiliary,
  suggestExcludedBones,
  suggestAccessoryBranchExclusions,
  axisFromLegChains,
  lateralAxis,
  chainSideHint,
  scoreCandidate,
  markAuxiliaryByGeometry,
  pruneInlineTwistBones,
} from './helpers';
import { detectRoot } from './phases/root';
import { pickBestPelvis } from './phases/pelvis';
import { findAppendageChains as findSharedAppendageChains } from './augmentations';
import {
  detectLegs,
  classifyLegType,
  shouldMirrorDigitigradeLegType,
  splitToeChain,
  collectExtraToes,
  trimPathAtFoot,
  trimLeadingLegConnector,
} from './phases/legs';
import { detectSpineNeckHead } from './phases/spine';
import {
  detectArms,
  extractClavicleFromArm,
  findExtraArmCandidates,
  shouldRetainNamedLowerArmOnExtendedChain,
  type ExtraArmCandidate,
} from './phases/arms';
import { detectFingers, hasNamedFingerDescendants } from './phases/fingers';

// ═══════════════════════════════════════════════════════════════════════
// Detected feature (augmentation tag)
// ═══════════════════════════════════════════════════════════════════════

export interface DetectedFeature {
  label: string;
  description: string;
}

// ═══════════════════════════════════════════════════════════════════════
// Main recognizer
// ═══════════════════════════════════════════════════════════════════════

export function recognizeSkeleton(
  skel: Skeleton,
  weightsInfo?: Map<number, boolean> | null,
  config?: RecognizerConfig | null,
): MappingResult {
  const t0 = performance.now();
  let prevPhase: string | null = null;
  let prevTime = t0;

  function log(msg: string): void {
    console.log(`[Cartographer] ${msg}`);
  }

  function startPhase(name: string): void {
    const now = performance.now();
    if (prevPhase !== null) {
      log(`${prevPhase} done in ${((now - prevTime) / 1000).toFixed(2)}s`);
    }
    log(name);
    prevPhase = name;
    prevTime = now;
  }

  log('Starting recognition...');

  const cfg = config ?? new RecognizerConfig();

  if (Object.keys(cfg.weights).length === 0) {
    cfg.weights = {
      topology: 0.30,
      symmetry: 0.20,
      direction: 0.20,
      length_ratio: 0.15,
      depth: 0.10,
      naming: 0.05,
    };
  }

  // ─── Prepare skeleton data ────────────────────────────────────────
  startPhase('Computing transforms...');
  skel.computeWorldTransforms();
  skel.computeDepthsAndSubtrees();
  skel.computeLengthsAndDirections();

  startPhase('Marking auxiliary bones...');
  markAuxiliary(skel, weightsInfo ?? null, cfg.lengthEpsilon);

  // Diagnostic: log which core bones were marked auxiliary by markAuxiliary
  const auxBones = skel.bones.filter((b) => b.auxiliary);
  const nonAuxBones = skel.bones.filter((b) => !b.auxiliary);
  log(`After markAuxiliary: ${auxBones.length} auxiliary, ${nonAuxBones.length} active`);
  if (nonAuxBones.length <= 40) {
    log(`Active bones: ${nonAuxBones.map((b) => b.name).join(', ')}`);
  }

  let autoExcluded: string[] = [];
  if (cfg.autoExclude) {
    autoExcluded = suggestExcludedBones(skel);
    if (autoExcluded.length > 0) {
      log(`Auto-excluding ${autoExcluded.length} helper bones: ${autoExcluded.join(', ')}`);
    }
  }

  const allExcluded: string[] = [
    ...cfg.excludedBones,
    ...autoExcluded.filter((n) => !cfg.excludedBones.includes(n)),
  ];
  for (const bone of skel.bones) {
    if (bone.auxiliary && !allExcluded.includes(bone.name)) {
      allExcluded.push(bone.name);
    }
  }

  const excludedSet = new Set(allExcluded.map((n) => n.toLowerCase()));
  if (excludedSet.size > 0) {
    for (const bone of skel.bones) {
      if (excludedSet.has(bone.name.toLowerCase())) {
        bone.auxiliary = true;
      }
    }
  }

  // ─── Spatial analysis ─────────────────────────────────────────────
  startPhase('Computing spatial analysis...');
  const spatialData = computeSpatialData(skel);
  const spatialLandmarks = computeSpatialLandmarks(skel, spatialData);

  // ─── Side naming convention ───────────────────────────────────────
  startPhase('Detecting side naming convention...');
  const boneNames = skel.bones.map((b) => b.name);
  let sideConvention = detectSideConvention(boneNames);
  if (sideConventionIsValid(sideConvention)) {
    log(`Side convention: ${sideConventionDescribe(sideConvention)}`);
  } else {
    log('No consistent side convention detected');
  }

  for (const bone of skel.bones) {
    bone.side = detectSideFromNameWithConvention(bone.name, sideConvention) as
      | 'Left'
      | 'Right'
      | null;
  }

  // ─── Adaptive scoring ─────────────────────────────────────────────
  if (cfg.useAdaptiveScoring && cfg.scoringProfile == null) {
    startPhase('Detecting naming mode for adaptive scoring...');
    const namingMode = detectNamingMode(boneNames);
    cfg.detectedNamingMode = namingMode;
    cfg.scoringProfile = getProfileForMode(namingMode);
    log(`Naming mode: ${namingMode} -> using ${cfg.scoringProfile.description}`);

    if (namingMode === NamingMode.GENERIC) {
      const leftPats = sideConvention.leftPatterns ?? [];
      const rightPats = sideConvention.rightPatterns ?? [];
      const suspiciousPatterns = ['_1', '_2', '_3', '_4', '1_', '2_', '3_', '4_'];
      const isSuspicious = [...leftPats, ...rightPats].some((p) =>
        suspiciousPatterns.includes(p),
      );

      if (isSuspicious) {
        log('Clearing side convention for generic naming (suspicious patterns)');
        for (const bone of skel.bones) {
          bone.side = null;
        }
        sideConvention = sideConventionUnknown();
      }
    }
  }

  const useSpatialSideFallback = cfg.shouldUseSpatialSideFallback();
  const scoringProfile = cfg.scoringProfile;

  let useSpatialChainFallback = false;
  if (cfg.detectedNamingMode != null) {
    useSpatialChainFallback = cfg.detectedNamingMode === NamingMode.GENERIC;
  }

  // ─── Utility closures ─────────────────────────────────────────────
  function boneRef(idx: number): string {
    const b = skel.bones[idx];
    if (!b) {
      console.warn(`boneRef: index ${idx} out of range (${skel.bones.length} bones)`);
      return `<unknown_${idx}>`;
    }
    return skel.refForBoneIndex(idx);
  }

  function chainRefs(indices: number[]): string[] {
    return indices.map((idx) => boneRef(idx));
  }

  function shouldDetectFingerChains(handIdx: number): boolean {
    const handNameLower = skel.bone(handIdx).name.toLowerCase();
    return (
      useSpatialChainFallback ||
      isHandLikeName(handNameLower) ||
      hasNamedFingerDescendants(skel, handIdx)
    );
  }

  function resolveRefToIndex(ref: string): number | null {
    return skel.resolveBoneRef(ref, { namespace: 'internal' });
  }

  function validChain(chain: number[] | null | undefined): number[] | null {
    if (!chain || chain.length === 0) return null;
    const filtered = chain.filter((idx) => idx >= 0 && idx < skel.bones.length);
    return filtered.length > 0 ? filtered : null;
  }

  function shouldPreserveInlineJoints(chain: number[], family: 'arm' | 'leg'): boolean {
    let structuralInlineCount = 0;
    for (const idx of chain) {
      const name = skel.bone(idx).name.toLowerCase();
      const isInlineDeformation =
        isTwistLikeName(name) || name.includes('roll');
      if (!isInlineDeformation) continue;
      if (family === 'arm') {
        if (isStructuralArmAuxiliaryName(name)) {
          structuralInlineCount += 1;
        }
      } else if (isStructuralLegAuxiliaryName(name)) {
        structuralInlineCount += 1;
      }
    }
    return structuralInlineCount >= 2;
  }

  function chainForRoleFamily(chain: number[], family: 'arm' | 'leg'): number[] {
    return shouldPreserveInlineJoints(chain, family)
      ? [...chain]
      : pruneInlineTwistBones(skel, chain);
  }

  function pathBetweenAncestors(startIdx: number, targetIdx: number): number[] | null {
    const reversePath: number[] = [];
    let current: number | null = targetIdx;
    while (current !== null) {
      reversePath.push(current);
      if (current === startIdx) {
        return reversePath.reverse();
      }
      current = skel.bone(current).parent;
    }

    const path: number[] = [];
    const visited = new Set<number>();

    function dfs(idx: number): boolean {
      if (visited.has(idx)) return false;
      visited.add(idx);
      path.push(idx);
      if (idx === targetIdx) return true;

      for (const childIdx of skel.bone(idx).children) {
        const childName = skel.bone(childIdx).name.toLowerCase();
        if (
          childIdx !== targetIdx &&
          isTerminalHelperLikeName(childName)
        ) {
          continue;
        }
        if (dfs(childIdx)) return true;
      }

      path.pop();
      return false;
    }

    return dfs(startIdx) ? [...path] : null;
  }

  function prependStructuralArmAncestors(chain: number[]): number[] {
    if (chain.length === 0) return chain;
    const ancestors: number[] = [];
    let current = skel.bone(chain[0]).parent;

    while (current !== null) {
      const bone = skel.bone(current);
      const name = bone.name.toLowerCase();
      if (isClavicleLikeName(name) && !isTwistLikeName(name)) break;
      if (!isStructuralArmChainName(name) || isTerminalHelperLikeName(name)) break;
      ancestors.push(current);
      current = bone.parent;
    }

    if (ancestors.length === 0) return chain;
    const twistAncestorCount = ancestors.filter((idx) =>
      isTwistLikeName(skel.bone(idx).name),
    ).length;
    if (twistAncestorCount < 2) {
      return chain;
    }
    return [...ancestors.reverse(), ...chain];
  }

  function shouldTrimLeadingClavicleLikeArmBone(
    chain: number[],
    explicitClavicleIdx: number | null,
  ): boolean {
    if (chain.length === 0) return false;

    const firstBone = skel.bone(chain[0]);
    const firstName = firstBone.name.toLowerCase();
    if (!isClavicleLikeName(firstName) || isTwistLikeName(firstName)) {
      return false;
    }

    if (explicitClavicleIdx === null) return true;
    if (chain.length < 2) return false;

    const secondName = skel.bone(chain[1]).name.toLowerCase();
    const lowerArmTerms = [
      ...(ROLE_TERMS['LowerArm'] ?? []),
      'elbow',
      'forearm',
    ];
    const leadingShoulderLooksLikeUpperArm =
      chain.length >= 3 &&
      firstName.includes('shoulder') &&
      lowerArmTerms.some((term) => secondName.includes(term));
    if (leadingShoulderLooksLikeUpperArm) {
      return false;
    }

    const hasPreservedInlineArmSegments = chain
      .slice(1, -1)
      .some((idx) => {
        const name = skel.bone(idx).name.toLowerCase();
        return isTwistLikeName(name) || name.includes('roll');
      });
    if (hasPreservedInlineArmSegments) {
      return false;
    }

    const nonAuxChildren = firstBone.children.filter(
      (childIdx) => !skel.bone(childIdx).auxiliary,
    );
    if (nonAuxChildren.length > 1) {
      return false;
    }
    return true;
  }

  function buildHeadChain(headStartIdx: number): number[] {
    const headTerms = ROLE_TERMS['Head'] ?? [];
    if (!isSimpleHeadSegmentName(skel.bone(headStartIdx).name)) {
      return [headStartIdx];
    }

    const chain = [headStartIdx];
    const visited = new Set(chain);
    let current = headStartIdx;

    while (true) {
      const candidates = skel
        .bone(current)
        .children.filter((childIdx) => {
          if (visited.has(childIdx)) return false;
          const child = skel.bone(childIdx);
          if (child.auxiliary) return false;
          const name = child.name.toLowerCase();
          if (isHeadDecorativeName(name)) return false;
          return (
            headTerms.some((term) => name.includes(term)) &&
            isSimpleHeadSegmentName(name)
          );
        })
        .sort((a, b) => skel.bone(b).subtreeSize - skel.bone(a).subtreeSize);

      if (candidates.length !== 1) break;
      current = candidates[0];
      visited.add(current);
      chain.push(current);
    }

    return chain;
  }

  // ─── Result containers ────────────────────────────────────────────
  const roleToBone: Record<string, string> = {};
  const roleToConfidence: Record<string, number> = {};
  const explain: Record<string, object> = {};
  const warnings: string[] = [];
  const chains: Record<string, string[]> = {};
  const chainResults: Record<string, ChainResult> = {};
  const roleCandidates: Record<string, CandidateInfo[]> = {};
  const detectedFeatures: DetectedFeature[] = [];
  const detectionMethods: Record<string, string> = {};

  function det(phase: string, detail: string): string {
    return `${phase}: ${detail}`;
  }

  function namingDetail(candidate: CandidateInfo): string {
    if (candidate.hasCategory('naming')) {
      for (const [state, reason] of candidate.getCategoryChecks('naming')) {
        if (state === 'positive' && reason.includes('Name matches')) {
          const term = reason.replace('Name matches: ', '').split(' (')[0];
          return `naming match (${term})`;
        }
      }
    }
    return 'spatial/topology';
  }

  // ─── Role helpers ─────────────────────────────────────────────────
  function promoteRole(
    role: string,
    name: string | null | undefined,
    score: number | null | undefined,
    rtb: Record<string, string>,
    rtc: Record<string, number>,
  ): void {
    if (!name) return;
    const scoreVal = score ?? 0.0;
    const current = rtc[role] ?? 0.0;
    if (scoreVal > current) {
      rtb[role] = name;
      rtc[role] = scoreVal;
    }
  }

  function setRole(
    role: string,
    name: string | null | undefined,
    score: number | null | undefined,
    rtb: Record<string, string>,
    rtc: Record<string, number>,
  ): void {
    if (!name) return;
    rtb[role] = name;
    rtc[role] = score ?? rtc[role] ?? 0.0;
  }

  function syncSingleBoneChain(
    chainName: string,
    boneName: string,
    confidence: number,
  ): void {
    const existing = chainResults[chainName];
    chains[chainName] = [boneName];
    chainResults[chainName] = new ChainMapping({
      name: chainName,
      boneNames: [boneName],
      startBone: boneName,
      endBone: boneName,
      ikGoal: existing?.ikGoal ?? CHAIN_DEFINITIONS[chainName]?.ikGoal ?? null,
      confidence,
      solverType: existing?.solverType ?? null,
      solverParams: existing?.solverParams ?? {},
      solverProperties: existing?.solverProperties ?? null,
      legType: existing?.legType ?? null,
    });
  }

  function buildRoleCandidates(): Record<string, [string, number]> {
    const roles = ['Pelvis', 'Head', 'LeftHand', 'RightHand', 'LeftFoot', 'RightFoot'];
    const candidates: Record<string, [string, number]> = {};
    for (const role of roles) {
      let best: [string, number] | null = null;
      for (const bone of skel.bones) {
        if (bone.auxiliary) continue;
        if ((role === 'Pelvis' || role === 'Head') && bone.hasSide) continue;
        const candidate = scoreCandidate(
          skel,
          bone.index,
          role,
          null,
          null,
          null,
          null,
          roleToBone,
          sideConvention,
          spatialData,
          useSpatialSideFallback,
          scoringProfile,
        );
        if (best === null || candidate.score > best[1]) {
          best = [boneRef(bone.index), candidate.score];
        }
      }
      if (best) candidates[role] = best;
    }
    return candidates;
  }

  function applyChainRoles(
    rtb: Record<string, string>,
    rtc: Record<string, number>,
    crs: Record<string, ChainResult>,
  ): void {
    const toeChainsPresent = new Set(
      Object.keys(crs).filter((n) => n.endsWith('Toe')),
    );

    function isValidForToeRole(_role: string, bn: string | null | undefined): boolean {
      if (!bn) return false;
      return isToeRoleCompatibleName(bn);
    }

    for (const [chainName, result] of Object.entries(crs)) {
      const chainDef = CHAIN_DEFINITIONS[chainName];
      if (!chainDef) continue;

      const chainBoneNames =
        result.boneNames.length > 0
          ? result.boneNames
          : result.startBone === result.endBone
            ? [result.startBone]
            : [result.startBone, result.endBone];
      const lastBoneIndex = chainBoneNames.length - 1;
      const conf = result.confidence ?? 0.0;

      for (const { boneIndex, roleName } of getChainRoleAssignments(
        chainName,
        chainBoneNames.length,
      )) {
        const boneName = chainBoneNames[boneIndex];
        if (!boneName) continue;

        if (roleName.includes('Toe')) {
          if (chainName === 'LeftLeg' || chainName === 'RightLeg') {
            const side = chainName.startsWith('Left') ? 'Left' : 'Right';
            if (toeChainsPresent.has(`${side}Toe`)) continue;
          }
          if (!isValidForToeRole(roleName, boneName)) continue;
        }

        const isEndpointAssignment =
          boneIndex === 0 || boneIndex === lastBoneIndex;
        if (!isEndpointAssignment && roleName in rtb) continue;

        promoteRole(roleName, boneName, conf, rtb, rtc);
      }
    }
  }

  function resolveRoleRef(ref: string | null | undefined): number | null {
    if (!ref) return null;
    return skel.resolveBoneRef(ref, { namespace: 'either' });
  }

  function isStrictAncestor(ancestorIdx: number, descendantIdx: number): boolean {
    let current = skel.bone(descendantIdx).parent;
    while (current !== null) {
      if (current === ancestorIdx) return true;
      current = skel.bone(current).parent;
    }
    return false;
  }

  function firstResolvedRef(refs: Array<string | null | undefined>): number | null {
    for (const ref of refs) {
      const idx = resolveRoleRef(ref);
      if (idx !== null) return idx;
    }
    return null;
  }

  function pelvisHasBipedStructure(
    pelvisRef: string | null | undefined,
    rtb: Record<string, string>,
  ): boolean {
    const pelvisIdx = resolveRoleRef(pelvisRef);
    if (pelvisIdx === null) return false;

    const leftLegIdx = firstResolvedRef([
      rtb.LeftUpperLeg,
      chains.LeftLeg?.[0],
      rtb.LeftLowerLeg,
      rtb.LeftFoot,
    ]);
    const rightLegIdx = firstResolvedRef([
      rtb.RightUpperLeg,
      chains.RightLeg?.[0],
      rtb.RightLowerLeg,
      rtb.RightFoot,
    ]);
    const torsoRefs = [
      rtb.Spine,
      ...(chains.Spine ?? []),
      rtb.Chest,
      rtb.Neck,
      rtb.Head,
    ];

    if (leftLegIdx === null || rightLegIdx === null) return false;
    const ownsLegs =
      isStrictAncestor(pelvisIdx, leftLegIdx) &&
      isStrictAncestor(pelvisIdx, rightLegIdx);
    const ownsTorso = torsoRefs.some((ref) => {
      const torsoIdx = resolveRoleRef(ref);
      return torsoIdx !== null && isStrictAncestor(pelvisIdx, torsoIdx);
    });

    return ownsLegs && ownsTorso;
  }

  function shouldPromotePelvisCandidate(
    currentName: string,
    candidateName: string,
    rtb: Record<string, string>,
  ): boolean {
    const currentIsNamedPelvis = isPelvisLikeName(currentName);
    const candidateIsNamedPelvis = isPelvisLikeName(candidateName);
    const currentIsStructural = pelvisHasBipedStructure(currentName, rtb);
    const candidateIsStructural = pelvisHasBipedStructure(candidateName, rtb);

    if (candidateIsStructural && !currentIsStructural) return true;
    if (currentIsStructural && !candidateIsStructural) return false;
    if (candidateIsNamedPelvis && !currentIsNamedPelvis) {
      return candidateIsStructural || !currentIsStructural;
    }
    return false;
  }

  function nearestStructuralPelvisRef(
    currentName: string,
    rtb: Record<string, string>,
  ): string | null {
    const currentIdx = resolveRoleRef(currentName);
    if (currentIdx === null) return null;

    const rootIdx = resolveRoleRef(rtb.Root);
    const candidateIndices = [currentIdx, ...skel.ancestors(currentIdx)];

    for (const candidateIdx of candidateIndices) {
      if (candidateIdx === rootIdx) continue;

      const candidateRef = boneRef(candidateIdx);
      if (pelvisHasBipedStructure(candidateRef, rtb)) {
        return candidateRef;
      }
    }

    return null;
  }

  function scoreRoleRef(
    role: string,
    ref: string,
    rtb: Record<string, string>,
  ): number {
    const idx = resolveRoleRef(ref);
    if (idx === null) return rtb[role] ? roleToConfidence[role] ?? 1.0 : 1.0;

    return scoreCandidate(
      skel,
      idx,
      role,
      null,
      null,
      null,
      null,
      rtb,
      sideConvention,
      spatialData,
      useSpatialSideFallback,
      scoringProfile,
    ).score;
  }

  function removePelvisBridgeFromSpineChain(
    rtb: Record<string, string>,
    rtc: Record<string, number>,
  ): void {
    const pelvisRef = rtb.Pelvis;
    const spineChain = chains.Spine;
    if (!pelvisRef || !spineChain || spineChain.length < 2) return;
    if (spineChain[0] !== pelvisRef) return;

    const trimmed = spineChain.slice(1);
    const existing = chainResults.Spine;

    chains.Spine = trimmed;
    chainResults.Spine = new ChainMapping({
      name: 'Spine',
      startBone: trimmed[0],
      endBone: trimmed[trimmed.length - 1],
      boneNames: trimmed,
      ikGoal: existing?.ikGoal ?? null,
      confidence: existing?.confidence ?? rtc.Spine ?? 1.0,
      solverType: existing?.solverType ?? null,
      solverParams: existing?.solverParams ?? {},
      solverProperties: existing?.solverProperties ?? null,
      legType: existing?.legType ?? null,
    });

    setRole('Spine', trimmed[0], scoreRoleRef('Spine', trimmed[0], rtb), rtb, rtc);
  }

  function applyRoleCandidates(
    rtb: Record<string, string>,
    rtc: Record<string, number>,
    candidates: Record<string, [string, number]>,
  ): void {
    const usedBones = new Set(
      Object.values(rtb).map((n) => n.toLowerCase()),
    );

    for (const [role, [name, score]] of Object.entries(candidates)) {
      const currentName = rtb[role];

      if (role === 'Pelvis' && currentName) {
        const structuralPelvisRef = nearestStructuralPelvisRef(currentName, rtb);
        if (structuralPelvisRef && structuralPelvisRef !== currentName) {
          const structuralPelvisScore = scoreRoleRef(
            role,
            structuralPelvisRef,
            rtb,
          );
          setRole(
            role,
            structuralPelvisRef,
            structuralPelvisScore,
            rtb,
            rtc,
          );
          syncSingleBoneChain('Pelvis', structuralPelvisRef, structuralPelvisScore);
          usedBones.add(structuralPelvisRef.toLowerCase());
          removePelvisBridgeFromSpineChain(rtb, rtc);
        } else if (shouldPromotePelvisCandidate(currentName, name, rtb)) {
          setRole(role, name, score, rtb, rtc);
          syncSingleBoneChain('Pelvis', name, score);
          usedBones.add(name.toLowerCase());
          removePelvisBridgeFromSpineChain(rtb, rtc);
        }
        continue;
      }

      if (!(role in rtb)) {
        if (usedBones.has(name.toLowerCase())) continue;
        promoteRole(role, name, score, rtb, rtc);
        usedBones.add(name.toLowerCase());
      }
    }
  }

  function shouldDropSyntheticRootRole(rtb: Record<string, string>): boolean {
    const rootRef = rtb.Root;
    const pelvisRef = rtb.Pelvis;
    if (!rootRef || !pelvisRef) return false;

    const rootIdx = resolveRoleRef(rootRef);
    const pelvisIdxResolved = resolveRoleRef(pelvisRef);
    if (rootIdx === null || pelvisIdxResolved === null) return false;
    if (!skel.bone(rootIdx).auxiliary) return false;
    if (!isStrictAncestor(rootIdx, pelvisIdxResolved)) return false;
    const pelvisName = skel.bone(pelvisIdxResolved).name.toLowerCase();
    const rootHints = cfg.namingHints['Root'] ?? ['root'];
    const pelvisLooksLikeRoot = rootHints.some((hint) => pelvisName.includes(hint.toLowerCase()));
    if (!pelvisLooksLikeRoot) return false;

    return pelvisHasBipedStructure(pelvisRef, rtb);
  }

  function filterExcludedUsed(mapping: MappingResult): MappingResult {
    if (!mapping.excludedBones || mapping.excludedBones.length === 0) return mapping;
    const used = new Set(
      Object.values(mapping.roleToBone).map((n) => n.toLowerCase()),
    );
    for (const chain of Object.values(mapping.chains)) {
      for (const bn of chain) used.add(bn.toLowerCase());
    }
    mapping.excludedBones = mapping.excludedBones.filter(
      (n) => !used.has(n.toLowerCase()),
    );
    return mapping;
  }

  function excludedNameTokensToRefs(tokens: string[]): string[] {
    const refs: string[] = [];
    const seen = new Set<string>();
    for (const token of tokens) {
      const trimmed = token.trim();
      if (!trimmed) continue;

      const directIdx = skel.resolveBoneRef(trimmed, { namespace: 'internal' });
      if (directIdx !== null) {
        const ref = skel.refForBoneIndex(directIdx);
        if (!seen.has(ref)) {
          seen.add(ref);
          refs.push(ref);
        }
        continue;
      }

      const tokenLower = trimmed.toLowerCase();
      for (const bone of skel.bones) {
        if (bone.name.toLowerCase() !== tokenLower) continue;
        const ref = bone.internalRef;
        if (seen.has(ref)) continue;
        seen.add(ref);
        refs.push(ref);
      }
    }
    return refs;
  }

  // ═══════════════════════════════════════════════════════════════════
  // RIG PRESET DETECTION (Fast Path)
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Checking for rig preset...');
  const presetMatch = detectRigPreset(skel);

  if (presetMatch) {
    log(`Detected rig preset: ${presetMatch.preset.name}`);
    const presetResult = buildResultFromRigPreset(skel, presetMatch, allExcluded);

    const hasLeftLeg =
      presetResult.chains['LeftLeg'] != null &&
      presetResult.chains['LeftLeg'].length >= 2;
    const hasRightLeg =
      presetResult.chains['RightLeg'] != null &&
      presetResult.chains['RightLeg'].length >= 2;

    if (hasLeftLeg && hasRightLeg) {
      return filterExcludedUsed(presetResult);
    }
    log(
      `Preset ${presetMatch.preset.name} missing leg chains, falling back to heuristics`,
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // HEURISTIC DETECTION (Standard Path)
  // ═══════════════════════════════════════════════════════════════════
  startPhase('No rig preset detected, heuristic detection');

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 1: PELVIS DETECTION
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 1: Pelvis detection');
  const [pelvisIdx, _pelvisBreakdown, initialLegChains] = pickBestPelvis(skel, cfg, spatialData);
  const pelvis = skel.bone(pelvisIdx);

  const pelvisCandidate = scoreCandidate(
    skel,
    pelvisIdx,
    'Pelvis',
    null,
    null,
    1,
    null,
    roleToBone,
    sideConvention,
    spatialData,
    useSpatialSideFallback,
    scoringProfile,
  );

  roleToBone['Pelvis'] = boneRef(pelvisIdx);
  roleToConfidence['Pelvis'] = pelvisCandidate.score;
  roleCandidates['Pelvis'] = [pelvisCandidate];
  detectionMethods['Pelvis'] = det(
    'Pelvis',
    `spatial branching analysis, ${namingDetail(pelvisCandidate)}`,
  );
  chains['Pelvis'] = [boneRef(pelvisIdx)];
  chainResults['Pelvis'] = new ChainMapping({
    name: 'Pelvis',
    boneNames: [boneRef(pelvisIdx)],
    startBone: boneRef(pelvisIdx),
    endBone: boneRef(pelvisIdx),
    confidence: pelvisCandidate.score,
  });

  if (pelvisCandidate.score < cfg.getEffectiveMinConfidence()) {
    warnings.push('Pelvis confidence below minimum.');
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 2: AXIS DETECTION
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 2: Axis detection');
  let downAxis = axisFromLegChains(skel, initialLegChains);
  let upAxis = downAxis.clone().negate();
  let latAxis = lateralAxis(skel, pelvisIdx, initialLegChains);

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 3: ROOT DETECTION
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 3: Root detection');
  const rootResult = detectRoot(skel, pelvisIdx, cfg, spatialData);
  if (rootResult) {
    const [rootIdx, rootCandidate] = rootResult;
    roleToBone['Root'] = boneRef(rootIdx);
    roleToConfidence['Root'] = rootCandidate.score;
    roleCandidates['Root'] = [rootCandidate];
    detectionMethods['Root'] = det('Root', 'parent of Pelvis');

    chains['Root'] = [boneRef(rootIdx)];
    chainResults['Root'] = new ChainMapping({
      name: 'Root',
      boneNames: [boneRef(rootIdx)],
      startBone: boneRef(rootIdx),
      endBone: boneRef(rootIdx),
      confidence: rootCandidate.score,
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 4: LEG CHAINS
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 4: Leg detection');
  let [leftLeg, rightLeg, legSymmetry] = detectLegs(skel, pelvis, latAxis, downAxis, cfg, spatialData);
  if (leftLeg && rightLeg) {
    const leftHint = chainSideHint(skel, leftLeg);
    const rightHint = chainSideHint(skel, rightLeg);
    if (
      (leftHint === 'Right' && rightHint !== 'Right') ||
      (rightHint === 'Left' && leftHint !== 'Left')
    ) {
      [rightLeg, leftLeg] = [leftLeg, rightLeg];
    }
  }

  let legLateralSpread: number | null = null;
  if (leftLeg && rightLeg) {
    const pelvisPos = new Vector3().setFromMatrixPosition(pelvis.restWorld);
    const leftStartPos = new Vector3().setFromMatrixPosition(
      skel.bone(leftLeg[0]).restWorld,
    );
    const rightStartPos = new Vector3().setFromMatrixPosition(
      skel.bone(rightLeg[0]).restWorld,
    );

    const allPositions: Vector3[] = skel.bones.map((b) =>
      new Vector3().setFromMatrixPosition(b.restWorld),
    );
    let lateralExtent = 1.0;
    if (allPositions.length > 0) {
      const dots = allPositions.map((p) => p.dot(latAxis));
      lateralExtent = Math.max(...dots) - Math.min(...dots);
    }
    const minSpread = Math.max(0.05 * lateralExtent, 1.0);
    legLateralSpread = lateralSpreadScore(
      leftStartPos,
      rightStartPos,
      latAxis,
      pelvisPos,
      minSpread,
    );

    const leftX = skel.bone(leftLeg[0]).restWorld.elements[12];
    const rightX = skel.bone(rightLeg[0]).restWorld.elements[12];
    spatialData.leftIsLowerX = leftX < rightX;
    spatialData.centerX = (leftX + rightX) / 2.0;
  }

  let leftLegType = leftLeg ? classifyLegType(skel, leftLeg, downAxis, latAxis) : null;
  let rightLegType = rightLeg ? classifyLegType(skel, rightLeg, downAxis, latAxis) : null;
  if (
    leftLeg &&
    rightLeg &&
    leftLegType !== rightLegType
  ) {
    if (
      leftLegType === 'digitigrade' &&
      rightLegType === 'plantigrade' &&
      shouldMirrorDigitigradeLegType(skel, leftLeg, rightLeg, legSymmetry)
    ) {
      rightLegType = 'digitigrade';
    } else if (
      rightLegType === 'digitigrade' &&
      leftLegType === 'plantigrade' &&
      shouldMirrorDigitigradeLegType(skel, rightLeg, leftLeg, legSymmetry)
    ) {
      leftLegType = 'digitigrade';
    }
  }

  for (const [side, legChainRaw] of [
    ['Left', leftLeg],
    ['Right', rightLeg],
  ] as const) {
    if (!legChainRaw) continue;

    let legChain = chainForRoleFamily(trimLeadingLegConnector(skel, legChainRaw), 'leg');
    legChain = trimPathAtFoot(skel, legChain);

    let legType = side === 'Left' ? leftLegType : rightLegType;
    if (legType == null) {
      legType = classifyLegType(skel, legChain, downAxis, latAxis);
    }

    let toeIdx: number | null = null;
    if (legType === 'digitigrade') {
      if (legChain.length >= 5) {
        toeIdx = legChain[legChain.length - 1];
        legChain = legChain.slice(0, -1);
      } else if (legChain.length === 4) {
        toeIdx = null;
      } else {
        legType = 'plantigrade';
        const [lc, ti] = splitToeChain(skel, legChain);
        legChain = lc;
        toeIdx = ti;
      }
    } else {
      const [lc, ti] = splitToeChain(skel, legChain);
      legChain = lc;
      toeIdx = ti;
    }

    if (legChain.length < 3) continue;

    const chainName = `${side}Leg`;
    chains[chainName] = chainRefs(legChain);

    const chainDef = CHAIN_DEFINITIONS[chainName];
    const legSolverType = legType === 'digitigrade' ? SolverType.Z : null;
    chainResults[chainName] = new ChainMapping({
      name: chainName,
      boneNames: chainRefs(legChain),
      startBone: boneRef(legChain[0]),
      endBone: boneRef(legChain[legChain.length - 1]),
      confidence: legSymmetry,
      ikGoal: chainDef?.ikGoal ?? null,
      solverType: legSolverType,
      legType,
    });

    const upperKeywords = ROLE_TERMS['UpperLeg'] ?? [];
    let upper: number | null = null;
    for (const boneIdx of legChain.slice(0, -1)) {
      const bn = skel.bone(boneIdx).name.toLowerCase();
      if (upperKeywords.some((kw) => bn.includes(kw))) {
        upper = boneIdx;
        break;
      }
    }
    if (upper === null) {
      for (const boneIdx of legChain.slice(0, -1)) {
        if (skel.bone(boneIdx).hasSide) {
          upper = boneIdx;
          break;
        }
      }
    }
    if (upper === null) upper = legChain[0];

    const upperPos = legChain.indexOf(upper);

    let legRolePositions: Record<string, [number | null, number]>;
    const exactLegAssignments = getChainRoleAssignments(`${side}Leg`, legChain.length);
    const exactLegRolePositions = Object.fromEntries(
      exactLegAssignments
        .filter(({ roleName, boneIndex }) =>
          boneIndex >= 0 &&
          boneIndex < legChain.length &&
          (
            roleName === `${side}UpperLeg` ||
            roleName === `${side}LowerLeg` ||
            roleName === `${side}Hock` ||
            roleName === `${side}Foot`
          ),
        )
        .map(({ roleName, boneIndex }) => [
          roleName,
          [legChain[boneIndex] ?? null, boneIndex + 1] as [number | null, number],
        ]),
    );

    if (Object.keys(exactLegRolePositions).length >= 3) {
      legRolePositions = exactLegRolePositions;
    } else if (legType === 'digitigrade') {
      const footKeywords = ROLE_TERMS['Foot'] ?? [];
      let foot: number | null = null;
      for (const boneIdx of legChain.slice(upperPos + 1)) {
        const bn = skel.bone(boneIdx).name.toLowerCase();
        if (footKeywords.some((kw) => bn.includes(kw))) {
          foot = boneIdx;
          break;
        }
      }
      if (foot === null && upperPos + 1 < legChain.length) {
        foot = legChain[legChain.length - 1];
      }
      const footPos =
        foot !== null && legChain.includes(foot)
          ? legChain.indexOf(foot)
          : legChain.length - 1;

      const hockKeywords = ROLE_TERMS['Hock'] ?? [];
      let hock: number | null = null;
      for (const boneIdx of legChain.slice(upperPos + 1, footPos)) {
        const bn = skel.bone(boneIdx).name.toLowerCase();
        if (hockKeywords.some((kw) => bn.includes(kw))) {
          hock = boneIdx;
          break;
        }
      }
      if (hock === null && footPos - 1 > upperPos) {
        hock = legChain[footPos - 1];
      }
      const hockPos =
        hock !== null && legChain.includes(hock)
          ? legChain.indexOf(hock)
          : Math.max(upperPos + 1, footPos - 1);

      const lowerKeywords = ROLE_TERMS['LowerLeg'] ?? [];
      let lower: number | null = null;
      for (const boneIdx of legChain.slice(upperPos + 1, hockPos)) {
        const bn = skel.bone(boneIdx).name.toLowerCase();
        if (lowerKeywords.some((kw) => bn.includes(kw))) {
          lower = boneIdx;
          break;
        }
      }
      if (lower === null && hockPos - 1 > upperPos) {
        lower = legChain[hockPos - 1];
      }
      if (lower === null && upperPos + 1 < legChain.length) {
        lower = legChain[upperPos + 1];
      }

      legRolePositions = {
        [`${side}UpperLeg`]: [upper, 1],
        [`${side}LowerLeg`]: [lower, 2],
        [`${side}Hock`]: [hock, 3],
        [`${side}Foot`]: [foot, 4],
      };

      if (!detectedFeatures.some((f) => f.label === 'Digitigrade Legs')) {
        detectedFeatures.push({
          label: 'Digitigrade Legs',
          description:
            'Detected hind-leg (digitigrade) anatomy: the leg chain was extended ' +
            'with a Hock joint between LowerLeg and Foot, producing an ' +
            'UpperLeg \u2192 LowerLeg \u2192 Hock \u2192 Foot \u2192 Toe chain.',
        });
      }
    } else {
      const lowerKeywords = ROLE_TERMS['LowerLeg'] ?? [];
      let lower: number | null = null;
      for (const boneIdx of legChain.slice(upperPos + 1, -1)) {
        const bn = skel.bone(boneIdx).name.toLowerCase();
        if (lowerKeywords.some((kw) => bn.includes(kw))) {
          lower = boneIdx;
          break;
        }
      }
      if (lower === null && upperPos + 1 < legChain.length) {
        lower = legChain[upperPos + 1];
      }

      const lowerPos =
        lower !== null && legChain.includes(lower)
          ? legChain.indexOf(lower)
          : upperPos + 1;

      const footKeywords = ROLE_TERMS['Foot'] ?? [];
      let foot: number | null = null;
      for (const boneIdx of legChain.slice(lowerPos + 1)) {
        const bn = skel.bone(boneIdx).name.toLowerCase();
        if (footKeywords.some((kw) => bn.includes(kw))) {
          foot = boneIdx;
          break;
        }
      }
      if (foot === null && lowerPos + 1 < legChain.length) {
        foot = legChain[lowerPos + 1];
      }

      legRolePositions = {
        [`${side}UpperLeg`]: [upper, 1],
        [`${side}LowerLeg`]: [lower, 2],
        [`${side}Foot`]: [foot, 3],
      };
    }

    const mirrorChainName = `${side === 'Left' ? 'Right' : 'Left'}Leg`;
    let mirrorChainIndices: number[] | null = null;
    if (mirrorChainName in chains) {
      const mirrorNames = chains[mirrorChainName];
      mirrorChainIndices = mirrorNames
        .map((ref) => resolveRefToIndex(ref))
        .filter((idx): idx is number => idx !== null);
    }

    for (const [role, [idx, chainPos]] of Object.entries(legRolePositions)) {
      if (idx === null) continue;
      const roleExpectedDir = role.includes('Foot') ? null : downAxis;

      const candidate = scoreCandidate(
        skel,
        idx,
        role,
        legChain,
        mirrorChainIndices,
        chainPos,
        roleExpectedDir,
        roleToBone,
        sideConvention,
        spatialData,
        useSpatialSideFallback,
        scoringProfile,
      );

      if (candidate.score >= cfg.getEffectiveMinConfidence()) {
        roleToBone[role] = boneRef(idx);
        roleToConfidence[role] = candidate.score;
        roleCandidates[role] = [candidate];
        detectionMethods[role] = det(
          'Legs',
          `chain position ${chainPos}/${legChain.length}, ${namingDetail(candidate)}`,
        );
      }
    }

    if (toeIdx !== null) {
      const toeName = `${side}Toe`;
      chains[toeName] = [boneRef(toeIdx)];
      chainResults[toeName] = new ChainMapping({
        name: toeName,
        boneNames: [boneRef(toeIdx)],
        startBone: boneRef(toeIdx),
        endBone: boneRef(toeIdx),
        confidence: legSymmetry,
      });

      const toeCandidate = scoreCandidate(
        skel,
        toeIdx,
        toeName,
        legChain,
        mirrorChainIndices,
        legChain.length + 1,
        null,
        roleToBone,
        sideConvention,
        spatialData,
        useSpatialSideFallback,
        scoringProfile,
      );

      roleToBone[toeName] = boneRef(toeIdx);
      roleToConfidence[toeName] = toeCandidate.score;
      roleCandidates[toeName] = [toeCandidate];
      detectionMethods[toeName] = det(
        'Toes',
        `split from leg chain, ${namingDetail(toeCandidate)}`,
      );
    }

    const footIdx = legChain[legChain.length - 1];
    const extraToes = collectExtraToes(skel, footIdx, side, toeIdx);
    for (const [etoeName, etoeChain] of Object.entries(extraToes)) {
      if (etoeName in chains) continue;
      chains[etoeName] = chainRefs(etoeChain);
      chainResults[etoeName] = new ChainMapping({
        name: etoeName,
        boneNames: chainRefs(etoeChain),
        startBone: boneRef(etoeChain[0]),
        endBone: boneRef(etoeChain[etoeChain.length - 1]),
        confidence: legSymmetry,
      });

      const extraToeCandidate = scoreCandidate(
        skel,
        etoeChain[0],
        etoeName,
        etoeChain,
        null,
        1,
        null,
        roleToBone,
        sideConvention,
        spatialData,
        useSpatialSideFallback,
        scoringProfile,
      );

      roleToBone[etoeName] = boneRef(etoeChain[0]);
      roleToConfidence[etoeName] = extraToeCandidate.score;
      roleCandidates[etoeName] = [extraToeCandidate];
      detectionMethods[etoeName] = det('Toes', `extra toe chain (${etoeName})`);
    }
  }

  function chainHasLegTokens(chain: number[]): boolean {
    const legTokens = new Set([
      ...(ROLE_TERMS['UpperLeg'] ?? []),
      ...(ROLE_TERMS['LowerLeg'] ?? []),
      ...(ROLE_TERMS['Foot'] ?? []),
      ...(ROLE_TERMS['Toe'] ?? []),
      'leg',
      'hip',
    ]);
    for (const idx of chain) {
      const name = skel.bone(idx).name.toLowerCase();
      for (const token of legTokens) {
        if (name.includes(token)) return true;
      }
    }
    return false;
  }

  if (leftLeg && rightLeg) {
    if (!initialLegChains.some((ch) => chainHasLegTokens(ch))) {
      downAxis = axisFromLegChains(skel, [leftLeg, rightLeg]);
      upAxis = downAxis.clone().negate();
      latAxis = lateralAxis(skel, pelvisIdx, [leftLeg, rightLeg]);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 5: SPINE, NECK, HEAD
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 5: Spine/Neck/Head detection');
  const [rawSpineChain, rawNeckChain, headIdx, chestIdx] = detectSpineNeckHead(
    skel,
    pelvis,
    leftLeg ?? [],
    rightLeg ?? [],
    upAxis,
    cfg,
    latAxis,
    spatialData,
    spatialLandmarks,
    useSpatialChainFallback,
  );
  let spineChain = validChain(rawSpineChain);
  const neckChain = validChain(rawNeckChain);

  if (spineChain && neckChain && neckChain.length > 0) {
    const neckStartPos = spineChain.indexOf(neckChain[0]);
    if (neckStartPos > 0) {
      spineChain = spineChain.slice(0, neckStartPos);
    }
  }

  if (spineChain) {
    chains['Spine'] = chainRefs(spineChain);
    chainResults['Spine'] = new ChainMapping({
      name: 'Spine',
      boneNames: chainRefs(spineChain),
      startBone: boneRef(spineChain[0]),
      endBone: boneRef(spineChain[spineChain.length - 1]),
      confidence: 0.9,
    });

    const spineCandidate = scoreCandidate(
      skel,
      spineChain[0],
      'Spine',
      spineChain,
      null,
      1,
      upAxis,
      roleToBone,
      sideConvention,
      spatialData,
      useSpatialSideFallback,
      scoringProfile,
    );
    roleToBone['Spine'] = boneRef(spineChain[0]);
    roleToConfidence['Spine'] = spineCandidate.score;
    roleCandidates['Spine'] = [spineCandidate];
    detectionMethods['Spine'] = det(
      'Spine',
      `chain position 1/${spineChain.length}, ${namingDetail(spineCandidate)}`,
    );

    if (spineChain.length > 1) {
      const chestCandidate = scoreCandidate(
        skel,
        spineChain[spineChain.length - 1],
        'Chest',
        spineChain,
        null,
        spineChain.length,
        upAxis,
        roleToBone,
        sideConvention,
        spatialData,
        useSpatialSideFallback,
        scoringProfile,
      );
      roleToBone['Chest'] = boneRef(spineChain[spineChain.length - 1]);
      roleToConfidence['Chest'] = chestCandidate.score;
      roleCandidates['Chest'] = [chestCandidate];
      detectionMethods['Chest'] = det(
        'Spine',
        `chain position ${spineChain.length}/${spineChain.length}, ${namingDetail(chestCandidate)}`,
      );
    }
  }

  if (neckChain) {
    chains['Neck'] = chainRefs(neckChain);
    chainResults['Neck'] = new ChainMapping({
      name: 'Neck',
      boneNames: chainRefs(neckChain),
      startBone: boneRef(neckChain[0]),
      endBone: boneRef(neckChain[neckChain.length - 1]),
      confidence: 0.9,
    });

    const neckCandidate = scoreCandidate(
      skel,
      neckChain[0],
      'Neck',
      neckChain,
      null,
      1,
      upAxis,
      roleToBone,
      sideConvention,
      spatialData,
      useSpatialSideFallback,
      scoringProfile,
    );
    if (neckCandidate.score >= cfg.getEffectiveMinConfidence()) {
      roleToBone['Neck'] = boneRef(neckChain[0]);
      roleToConfidence['Neck'] = neckCandidate.score;
      roleCandidates['Neck'] = [neckCandidate];
      detectionMethods['Neck'] = det(
        'Neck',
        `chain position 1/${neckChain.length}, ${namingDetail(neckCandidate)}`,
      );
    }
  }

  if (headIdx !== null) {
    const headChain = buildHeadChain(headIdx);
    const headRoleIdx = headChain[headChain.length - 1];
    chains['Head'] = chainRefs(headChain);
    chainResults['Head'] = new ChainMapping({
      name: 'Head',
      boneNames: chainRefs(headChain),
      startBone: boneRef(headChain[0]),
      endBone: boneRef(headRoleIdx),
      confidence: 0.9,
    });

    const headCandidate = scoreCandidate(
      skel,
      headRoleIdx,
      'Head',
      headChain,
      null,
      headChain.length,
      upAxis,
      roleToBone,
      sideConvention,
      spatialData,
      useSpatialSideFallback,
      scoringProfile,
    );
    if (headCandidate.score >= cfg.getEffectiveMinConfidence()) {
      roleToBone['Head'] = boneRef(headRoleIdx);
      roleToConfidence['Head'] = headCandidate.score;
      roleCandidates['Head'] = [headCandidate];
      detectionMethods['Head'] = det(
        'Head',
        `head chain terminal, ${namingDetail(headCandidate)}`,
      );
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 6: ARMS AND CLAVICLES
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 6: Arm detection');
  const headIsOnlyTorsoBranch =
    headIdx !== null &&
    (!spineChain || spineChain.length === 0) &&
    (!neckChain || neckChain.length === 0) &&
    chestIdx === null;
  const armNeckContext =
    neckChain && neckChain.length > 0
      ? neckChain
      : headIdx !== null
        ? [headIdx]
        : [];

  const [leftArmFull, rightArmFull, armSymmetry] = detectArms(
    skel,
    spineChain ?? [],
    chestIdx,
    pelvisIdx,
    latAxis,
    leftLeg ?? [],
    rightLeg ?? [],
    cfg,
    armNeckContext,
    spatialData,
    spatialLandmarks,
    useSpatialChainFallback,
  );

  let armLateralSpread: number | null = null;
  if (leftArmFull && rightArmFull) {
    const refPos = chestIdx != null
      ? new Vector3().setFromMatrixPosition(skel.bone(chestIdx).restWorld)
      : new Vector3().setFromMatrixPosition(pelvis.restWorld);
    const leftStartPos = new Vector3().setFromMatrixPosition(
      skel.bone(leftArmFull[0]).restWorld,
    );
    const rightStartPos = new Vector3().setFromMatrixPosition(
      skel.bone(rightArmFull[0]).restWorld,
    );
    const allPositions: Vector3[] = skel.bones.map((b) =>
      new Vector3().setFromMatrixPosition(b.restWorld),
    );
    let lateralExtent2 = 1.0;
    if (allPositions.length > 0) {
      const dots2 = allPositions.map((p) => p.dot(latAxis));
      lateralExtent2 = Math.max(...dots2) - Math.min(...dots2);
    }
    const minSpread2 = Math.max(0.05 * lateralExtent2, 1.0);
    armLateralSpread = lateralSpreadScore(
      leftStartPos,
      rightStartPos,
      latAxis,
      refPos,
      minSpread2,
    );
  }

  // ─── PASS 1: Register arm chains ─────────────────────────────────
  const armData: Record<
    string,
    { clavicleIdx: number | null; armChain: number[]; armIndices: number[] }
  > = {};

  for (const [side, armFull] of [
    ['Left', leftArmFull],
    ['Right', rightArmFull],
  ] as const) {
    if (!armFull) continue;

    const [clavicleIdx, armChainRaw] = extractClavicleFromArm(skel, armFull, spatialData);
    let expandedArmChainRaw = armChainRaw;
    if (clavicleIdx !== null && armChainRaw.length > 0) {
      const targetIdx = armChainRaw[armChainRaw.length - 1];
      const pathFromClavicle = pathBetweenAncestors(clavicleIdx, targetIdx);
      if (pathFromClavicle && pathFromClavicle.length > armChainRaw.length) {
        expandedArmChainRaw = pathFromClavicle.slice(1);
      }
    }
    let armChain = chainForRoleFamily(expandedArmChainRaw, 'arm');
    armChain = prependStructuralArmAncestors(armChain);

    while (shouldTrimLeadingClavicleLikeArmBone(armChain, clavicleIdx)) {
      armChain = armChain.slice(1);
    }

    if (headIsOnlyTorsoBranch && armChain.length < 3) {
      const armEndIdx = armChain[armChain.length - 1];
      const armEndName = skel.bone(armEndIdx).name.toLowerCase();
      const hasFunctionalHandEvidence =
        isHandLikeName(armEndName) ||
        hasNamedFingerDescendants(skel, armEndIdx);
      if (!hasFunctionalHandEvidence) continue;
    }

    if (armChain.length >= 2) {
      const armName = `${side}Arm`;
      chains[armName] = chainRefs(armChain);
      const chainDef2 = CHAIN_DEFINITIONS[armName];
      chainResults[armName] = new ChainMapping({
        name: armName,
        boneNames: chainRefs(armChain),
        startBone: boneRef(armChain[0]),
        endBone: boneRef(armChain[armChain.length - 1]),
        confidence: armSymmetry,
        ikGoal: chainDef2?.ikGoal ?? null,
      });
      armData[side] = { clavicleIdx, armChain, armIndices: [...armChain] };
    } else if (clavicleIdx !== null) {
      armData[side] = { clavicleIdx, armChain: [], armIndices: [] };
    }
  }

  // ─── PASS 2: Score arm roles ──────────────────────────────────────
  for (const side of ['Left', 'Right'] as const) {
    if (!(side in armData)) continue;

    const { clavicleIdx, armChain } = armData[side];

    if (clavicleIdx !== null) {
      const clavName = `${side}Clavicle`;
      chains[clavName] = [boneRef(clavicleIdx)];
      chainResults[clavName] = new ChainMapping({
        name: clavName,
        boneNames: [boneRef(clavicleIdx)],
        startBone: boneRef(clavicleIdx),
        endBone: boneRef(clavicleIdx),
        confidence: armSymmetry,
      });

      const clavCandidate = scoreCandidate(
        skel,
        clavicleIdx,
        clavName,
        null,
        null,
        1,
        side === 'Left' ? latAxis : latAxis.clone().negate(),
        roleToBone,
        sideConvention,
        spatialData,
        useSpatialSideFallback,
        scoringProfile,
      );
      roleToBone[clavName] = boneRef(clavicleIdx);
      roleToConfidence[clavName] = clavCandidate.score;
      roleCandidates[clavName] = [clavCandidate];
      detectionMethods[clavName] = det(
        'Arms',
        `extracted clavicle from arm chain, ${namingDetail(clavCandidate)}`,
      );
    }

    if (armChain.length < 2) continue;

    const upperArm = armChain[0];
    const hand = armChain[armChain.length - 1];
    const armChainDef = CHAIN_DEFINITIONS[`${side}Arm`];
    const exceedsCanonicalArmLength =
      armChainDef?.maxBones != null && armChain.length > armChainDef.maxBones;

    let lowerArm: number | null = null;
    let lowerArmMatchedByName = false;
    if (armChain.length === 2) {
      lowerArm = null;
    } else if (armChain.length === 3) {
      lowerArm = armChain[1];
    } else {
      const lowerArmTerms = [
        ...(ROLE_TERMS['LowerArm'] ?? []),
        'elbow',
        'forearm',
      ];
      for (const boneIdx of armChain.slice(1, -1)) {
        const name = skel.bone(boneIdx).name.toLowerCase();
        if (lowerArmTerms.some((term) => name.includes(term))) {
          lowerArm = boneIdx;
          lowerArmMatchedByName = true;
          break;
        }
      }

      const upperArmPos = new Vector3().setFromMatrixPosition(
        skel.bone(upperArm).restWorld,
      );
      const handPos = new Vector3().setFromMatrixPosition(
        skel.bone(hand).restWorld,
      );
      const totalDistance = handPos.distanceTo(upperArmPos);
      const targetDistance = totalDistance * 0.5;

      let bestLower: number | null = null;
      let bestScore = -Infinity;

      for (const boneIdx of lowerArm === null ? armChain.slice(1, -1) : []) {
        const bonePos = new Vector3().setFromMatrixPosition(
          skel.bone(boneIdx).restWorld,
        );
        const boneDistance = bonePos.distanceTo(upperArmPos);

        let midpointScore = 0;
        if (totalDistance > 1e-6) {
          midpointScore =
            1.0 - Math.abs(boneDistance - targetDistance) / totalDistance;
        }

        const prebend = jointPrebendRatio(
          upperArmPos,
          bonePos,
          handPos,
          latAxis,
          downAxis,
        );
        const prebendScore = Math.min(Math.abs(prebend) * 5.0, 1.0);

        const combined = 0.7 * midpointScore + 0.3 * prebendScore;
        if (combined > bestScore) {
          bestScore = combined;
          bestLower = boneIdx;
        }
      }

      lowerArm = lowerArm ?? bestLower ?? armChain[1];
    }

    if (
      exceedsCanonicalArmLength &&
      !shouldRetainNamedLowerArmOnExtendedChain(
        skel,
        armChain,
        lowerArm,
        lowerArmMatchedByName,
      )
    ) {
      lowerArm = null;
    }

    const armExpectedDir =
      side === 'Left' ? latAxis : latAxis.clone().negate();

    const armRolePositions: Record<string, [number | null, number]> = {
      [`${side}UpperArm`]: [upperArm, 1],
      [`${side}LowerArm`]: [lowerArm, 2],
      [`${side}Hand`]: [hand, 3],
    };

    const mirrorArmName = `${side === 'Left' ? 'Right' : 'Left'}Arm`;
    let mirrorArmIndices: number[] | null = null;
    if (mirrorArmName in chains) {
      const mirrorNames2 = chains[mirrorArmName];
      mirrorArmIndices = mirrorNames2
        .map((ref) => resolveRefToIndex(ref))
        .filter((idx): idx is number => idx !== null);
    }

    for (const [role, [idx, chainPos]] of Object.entries(armRolePositions)) {
      if (idx === null) continue;
      const roleExpectedDir = role.includes('Hand') ? null : armExpectedDir;

      const candidate = scoreCandidate(
        skel,
        idx,
        role,
        armChain,
        mirrorArmIndices,
        chainPos,
        roleExpectedDir,
        roleToBone,
        sideConvention,
        spatialData,
        useSpatialSideFallback,
        scoringProfile,
      );

      if (candidate.score >= cfg.getEffectiveMinConfidence()) {
        roleToBone[role] = boneRef(idx);
        roleToConfidence[role] = candidate.score;
        roleCandidates[role] = [candidate];
        detectionMethods[role] = det(
          'Arms',
          `chain position ${chainPos}/${armChain.length}, ${namingDetail(candidate)}`,
        );
      }
    }

    // ═══════════════════════════════════════════════════════════════════
    // PHASE 7: FINGER CHAINS (from hand)
    // ═══════════════════════════════════════════════════════════════════
    startPhase(`Phase 7: Finger detection for ${side}`);
    if (cfg.detectFingers && hand !== null) {
      if (shouldDetectFingerChains(hand)) {
        const [fingerChains, fingerEvidence] = detectFingers(
          skel,
          hand,
          side,
          cfg,
          spatialData,
          useSpatialChainFallback,
        );

        for (const [fingerName, fingerChain] of Object.entries(fingerChains)) {
          chains[fingerName] = chainRefs(fingerChain);
          chainResults[fingerName] = new ChainMapping({
            name: fingerName,
            boneNames: chainRefs(fingerChain),
            startBone: boneRef(fingerChain[0]),
            endBone: boneRef(fingerChain[fingerChain.length - 1]),
            confidence: 0.85,
          });

          const chainEvidence =
            fingerEvidence[fingerName] ?? 'Fingers: detected';

          for (const { boneIndex, roleName: role } of getChainRoleAssignments(
            fingerName,
            fingerChain.length,
          )) {
            const boneIdx = fingerChain[boneIndex];
            if (boneIdx == null) continue;

            const fingerCandidate = scoreCandidate(
              skel,
              boneIdx,
              role,
              fingerChain,
              null,
              boneIndex + 1,
              null,
              roleToBone,
              sideConvention,
              spatialData,
              useSpatialSideFallback,
              scoringProfile,
            );
            roleToBone[role] = boneRef(boneIdx);
            roleToConfidence[role] = fingerCandidate.score;
            roleCandidates[role] = [fingerCandidate];
            detectionMethods[role] = chainEvidence;
          }
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 6b: EXTRA ARM DETECTION
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 6b: Extra arm detection');

  const claimed = new Set<number>();
  for (const cb of Object.values(chains)) {
    for (const bn of cb) {
      const idx = resolveRefToIndex(bn);
      if (idx !== null) claimed.add(idx);
    }
  }

  const torsoSet = new Set<number>();
  const torso: number[] = [];
  function addTorso(idx: number): void {
    if (!torsoSet.has(idx)) {
      torsoSet.add(idx);
      torso.push(idx);
    }
  }
  addTorso(pelvisIdx);
  if (spineChain) {
    for (const si of spineChain) addTorso(si);
  }
  for (const acn of [
    'LeftClavicle',
    'RightClavicle',
    'LeftArm',
    'RightArm',
  ]) {
    if (acn in chains && chains[acn].length > 0) {
      const firstBn = chains[acn][0];
      const firstIdx = resolveRefToIndex(firstBn);
      if (firstIdx !== null) {
        let p: number | null = skel.bone(firstIdx).parent;
        while (p !== null) {
          addTorso(p);
          if (torsoSet.has(p) && skel.bone(p).parent !== null && torsoSet.has(skel.bone(p).parent!)) {
            break;
          }
          p = skel.bone(p).parent;
        }
      }
    }
  }

  const allPos: Vector3[] = skel.bones.map((b) =>
    new Vector3().setFromMatrixPosition(b.restWorld),
  );
  let latExtent = 1.0;
  if (allPos.length > 0) {
    const latDots = allPos.map((p) => p.dot(latAxis));
    latExtent = Math.max(...latDots) - Math.min(...latDots);
  }
  const pelvisPos = new Vector3().setFromMatrixPosition(pelvis.restWorld);

  const extraArmCandidates = findExtraArmCandidates(
    skel,
    torso,
    claimed,
    latAxis,
    pelvisPos,
    latExtent,
    upAxis,
  );

  if (extraArmCandidates.length > 0) {
    const leftExtras = extraArmCandidates.filter((c) => c.side === 'Left');
    const rightExtras = extraArmCandidates.filter((c) => c.side === 'Right');
    const maxExtraSets = 4;

    const assignments: [number, ExtraArmCandidate][] = [];
    const usedLeft = new Array(leftExtras.length).fill(false);
    const usedRight = new Array(rightExtras.length).fill(false);
    let nextSet = 2;

    for (let li = 0; li < leftExtras.length; li++) {
      if (nextSet > maxExtraSets + 1) break;
      const lc = leftExtras[li];
      let bestRi: number | null = null;
      let bestDist: number | null = null;
      for (let ri = 0; ri < rightExtras.length; ri++) {
        if (usedRight[ri]) continue;
        const dist = Math.abs(lc.spineParentIdx - rightExtras[ri].spineParentIdx);
        if (bestDist === null || dist < bestDist) {
          bestDist = dist;
          bestRi = ri;
        }
      }
      if (bestRi !== null && bestDist !== null && bestDist <= 1) {
        usedLeft[li] = true;
        usedRight[bestRi] = true;
        assignments.push([nextSet, lc]);
        assignments.push([nextSet, rightExtras[bestRi]]);
        nextSet++;
      }
    }

    for (let li = 0; li < leftExtras.length; li++) {
      if (nextSet > maxExtraSets + 1) break;
      if (!usedLeft[li]) {
        assignments.push([nextSet, leftExtras[li]]);
        nextSet++;
      }
    }
    for (let ri = 0; ri < rightExtras.length; ri++) {
      if (nextSet > maxExtraSets + 1) break;
      if (!usedRight[ri]) {
        assignments.push([nextSet, rightExtras[ri]]);
        nextSet++;
      }
    }

    const registeredSets = new Set<number>();
    for (const [setNum, cand] of assignments) {
      const suffix = `_${setNum}`;
      const eside = cand.side;
      const armNames = cand.armChain.map((i) => boneRef(i));
      const armChainName = `${eside}Arm${suffix}`;

      chains[armChainName] = armNames;
      chainResults[armChainName] = new ChainMapping({
        name: armChainName,
        boneNames: armNames,
        startBone: armNames[0],
        endBone: armNames[armNames.length - 1],
        confidence: armSymmetry ?? 0.85,
      });

      if (cand.clavicleIdx !== null) {
        const clavChainName = `${eside}Clavicle${suffix}`;
        const clavBoneName = boneRef(cand.clavicleIdx);
        chains[clavChainName] = [clavBoneName];
        chainResults[clavChainName] = new ChainMapping({
          name: clavChainName,
          boneNames: [clavBoneName],
          startBone: clavBoneName,
          endBone: clavBoneName,
          confidence: armSymmetry ?? 0.85,
        });
        roleToBone[`${eside}Clavicle${suffix}`] = clavBoneName;
        roleToConfidence[`${eside}Clavicle${suffix}`] = 0.85;
        detectionMethods[`${eside}Clavicle${suffix}`] = det(
          'Extra arms',
          `additional arm set ${setNum}, clavicle`,
        );
      }

      const extraArmDet = det(
        'Extra arms',
        `additional arm set ${setNum} from spine/torso`,
      );
      roleToBone[`${eside}UpperArm${suffix}`] = armNames[0];
      roleToConfidence[`${eside}UpperArm${suffix}`] = 0.85;
      detectionMethods[`${eside}UpperArm${suffix}`] = extraArmDet;
      roleToBone[`${eside}Hand${suffix}`] = armNames[armNames.length - 1];
      roleToConfidence[`${eside}Hand${suffix}`] = 0.85;
      detectionMethods[`${eside}Hand${suffix}`] = extraArmDet;
      if (armNames.length >= 3) {
        const mid = Math.floor(armNames.length / 2);
        roleToBone[`${eside}LowerArm${suffix}`] = armNames[mid];
        roleToConfidence[`${eside}LowerArm${suffix}`] = 0.85;
        detectionMethods[`${eside}LowerArm${suffix}`] = extraArmDet;
      }

      if (cfg.detectFingers) {
        const handIdx = cand.armChain[cand.armChain.length - 1];
        if (shouldDetectFingerChains(handIdx)) {
          const [extraFingers, extraFingerEvidence] = detectFingers(
            skel,
            handIdx,
            eside,
            cfg,
            spatialData,
            useSpatialChainFallback,
          );
          for (const [fn, fc] of Object.entries(extraFingers)) {
            const efn = `${fn}${suffix}`;
            chains[efn] = chainRefs(fc);
            chainResults[efn] = new ChainMapping({
              name: efn,
              boneNames: chainRefs(fc),
              startBone: boneRef(fc[0]),
              endBone: boneRef(fc[fc.length - 1]),
              confidence: 0.85,
            });
            const efe =
              extraFingerEvidence[fn] ?? 'Fingers: detected (extra arm)';
        for (const { boneIndex, roleName } of getChainRoleAssignments(
              efn,
              fc.length,
        )) {
          const boneIdx = fc[boneIndex];
          if (boneIdx == null) continue;
          roleToBone[roleName] = boneRef(boneIdx);
              roleToConfidence[roleName] = 0.85;
              detectionMethods[roleName] = efe;
            }
          }
        }
      }

      registeredSets.add(setNum);
    }

    const totalExtraSets = registeredSets.size;
    if (totalExtraSets > 0) {
      const label = totalExtraSets === 1 ? 'set' : 'sets';
      detectedFeatures.push({
        label: `Extra Arms (${totalExtraSets} extra ${label})`,
        description:
          `Detected ${totalExtraSets} additional ${label} of arms ` +
          `branching from the spine/torso, producing ` +
          `${totalExtraSets + 1} total arm sets.`,
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 6c: APPENDAGE DETECTION (tail, tentacle, antennae)
  // ═══════════════════════════════════════════════════════════════════
  startPhase('Phase 6c: Appendage detection');

  const appendages = findSharedAppendageChains(skel, chains);
  for (const ap of appendages) {
    chains[ap.chainName] = ap.boneNames;
    chainResults[ap.chainName] = new ChainMapping({
      name: ap.chainName,
      boneNames: ap.boneNames,
      startBone: ap.boneNames[0],
      endBone: ap.boneNames[ap.boneNames.length - 1],
      confidence: 0.85,
    });
    roleToBone[ap.startRole] = ap.boneNames[0];
    roleToConfidence[ap.startRole] = 0.85;
    roleToBone[ap.endRole] = ap.boneNames[ap.boneNames.length - 1];
    roleToConfidence[ap.endRole] = 0.85;
    const apDet = det(
      'Appendages',
      `${ap.appendageType} chain (${ap.boneNames.length} bones)`,
    );
    detectionMethods[ap.startRole] = apDet;
    detectionMethods[ap.endRole] = apDet;
  }

  if (appendages.length > 0) {
    const apTypeCounts: Record<string, number> = {};
    for (const ap of appendages) {
      apTypeCounts[ap.appendageType] =
        (apTypeCounts[ap.appendageType] ?? 0) + 1;
    }
    for (const [apType, apCount] of Object.entries(apTypeCounts)) {
      if (apCount === 1) {
        detectedFeatures.push({
          label: apType,
          description: `Detected ${apType.toLowerCase()} chain.`,
        });
      } else {
        detectedFeatures.push({
          label: `${apType} (${apCount})`,
          description: `Detected ${apCount} ${apType.toLowerCase()} chains.`,
        });
      }
    }
  }

  const mainBoneIndices = new Set<number>();
  for (const chainBones of Object.values(chains)) {
    for (const bn of chainBones) {
      const idx = resolveRefToIndex(bn);
      if (idx !== null) mainBoneIndices.add(idx);
    }
  }

  const accessoryExclusions = suggestAccessoryBranchExclusions(
    skel,
    mainBoneIndices,
  );
  for (const boneName of accessoryExclusions) {
    if (!allExcluded.includes(boneName)) allExcluded.push(boneName);
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 8: GEOMETRIC AUXILIARY DETECTION
  // ═══════════════════════════════════════════════════════════════════
  if (cfg.scoringProfile != null && cfg.scoringProfile.trustSpatialPelvis) {
    startPhase('Phase 8: Geometric auxiliary detection');

    const marked = markAuxiliaryByGeometry(
      skel,
      spatialData,
      mainBoneIndices,
    );
    if (marked.length > 0) {
      log(`Marked ${marked.length} bones as auxiliary (geometric)`);
      for (const boneIdx of marked) {
        const bn = skel.bone(boneIdx).name;
        if (!allExcluded.includes(bn)) allExcluded.push(bn);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // FINAL: ROLE RECONCILIATION
  // ═══════════════════════════════════════════════════════════════════
  const fallbackCandidates = buildRoleCandidates();
  applyChainRoles(roleToBone, roleToConfidence, chainResults);
  applyRoleCandidates(roleToBone, roleToConfidence, fallbackCandidates);

  if (shouldDropSyntheticRootRole(roleToBone)) {
    delete roleToBone.Root;
    delete roleToConfidence.Root;
    delete chains.Root;
    delete chainResults.Root;
    delete roleCandidates.Root;
    delete detectionMethods.Root;
  }

  for (const [role, score] of Object.entries(roleToConfidence)) {
    const tier = confidenceTier(score);
    if (tier === 'medium' || tier === 'low') {
      warnings.push(`${role} is ${tier} (score=${score.toFixed(2)}).`);
    }
  }

  const now = performance.now();
  if (prevPhase !== null) {
    log(`${prevPhase} done in ${((now - prevTime) / 1000).toFixed(2)}s`);
  }
  const total = (now - t0) / 1000;
  log(`Done! Found ${Object.keys(chains).length} chains in ${total.toFixed(2)}s`);

  const result = new MappingResult({
    roleToBone,
    roleToConfidence,
    chains,
    chainResults,
    warnings,
    explain: explain as Record<string, any>,
    detectedFeatures,
    excludedBones: excludedNameTokensToRefs(allExcluded),
    candidates: roleCandidates,
    roleToDetectionMethod: detectionMethods,
  });
  return filterExcludedUsed(result);
}

